import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaClipboardCheck } from 'react-icons/fa';
import { createAccountAct } from '../../Redux/Dashboard/External/AdminUsers/Actions/CreateAccountAction';
import { fetchAccountTypeMappingAct } from '../../Redux/Dashboard/External/AdminUsers/Actions/FetchAccountTypeMappingAction';
import countryOptions from '../../Constants/countryOptions';
import '../../Styles/AccountCreation.css';

const regionOptions = ['APJ', 'NA', 'EMEA','LATAM'];
const PARTNER_TYPE = 'Partner';
const OTHERS_TYPE = 'Others';

const AccountCreationForm = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isCreateAccountLoading = useSelector((state) => state.AdminCreateAccount?.loading);
    const createAccountError = useSelector((state) => state.AdminCreateAccount?.error);
    const typeOfUserOptions = useSelector((state) => state.AdminAccountTypeMapping?.data || []);
    const isTypeOfUserLoading = useSelector((state) => state.AdminAccountTypeMapping?.loading);
    const typeOfUserError = useSelector((state) => state.AdminAccountTypeMapping?.error);

    useEffect(() => {
        dispatch(fetchAccountTypeMappingAct()).catch(() => null);
    }, [dispatch]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        typeOfUser: '',
        company: '',
        businessUnit: 'DSOM',
        customBusinessUnit: '',
        otherTypeOfUser: '',
        country: '',
        managerName: '',
        region: '',
        isManager: 'No',
    });

    const normalizedTypeOfUserOptions = Array.from(new Set(
        typeOfUserOptions
            .filter((option) => typeof option === 'string')
            .map((option) => option.trim())
            .filter(Boolean)
    ));
    const accountTypeOptions = normalizedTypeOfUserOptions.includes(OTHERS_TYPE)
        ? normalizedTypeOfUserOptions
        : [...normalizedTypeOfUserOptions, OTHERS_TYPE];
    const isPartner = formData.typeOfUser === PARTNER_TYPE;
    const isOthers = formData.typeOfUser === OTHERS_TYPE;
    const isBmcCompanyType = Boolean(formData.typeOfUser) && !isPartner && !isOthers;
    const typeOfUserPlaceholder = isTypeOfUserLoading && normalizedTypeOfUserOptions.length === 0
        ? 'Loading...'
        : typeOfUserError && normalizedTypeOfUserOptions.length === 0
            ? 'Unable to load user types'
            : !isTypeOfUserLoading && normalizedTypeOfUserOptions.length === 0
                ? 'No user types available'
                : 'Select';

    const showCompanyInput = isPartner || isOthers;
    const showBusinessUnitOtherInput = formData.businessUnit === 'Other';
    const showOtherTypeOfUserInput = isOthers;

    const finalCompanyValue =
        isBmcCompanyType ? 'BMC' : formData.company.trim();

    const finalTypeOfUserValue = isOthers
        ? formData.otherTypeOfUser.trim()
        : formData.typeOfUser;

    const finalBusinessUnitValue =
        formData.businessUnit === 'Other'
            ? formData.customBusinessUnit.trim()
            : formData.businessUnit;

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const updated = {
                ...prev,
                [name]: value,
            };

            if (name === 'typeOfUser') {
                if (value && value !== PARTNER_TYPE) {
                    updated.company = value === OTHERS_TYPE ? '' : 'BMC';
                    updated.businessUnit = 'DSOM';
                    updated.customBusinessUnit = '';
                    updated.otherTypeOfUser = '';
                } else if (value === PARTNER_TYPE) {
                    updated.company = '';
                    updated.businessUnit = 'DSOM';
                    updated.customBusinessUnit = '';
                    updated.otherTypeOfUser = '';
                } else {
                    updated.company = '';
                    updated.businessUnit = 'DSOM';
                    updated.customBusinessUnit = '';
                    updated.otherTypeOfUser = '';
                }
            }

            if (name === 'businessUnit' && value !== 'Other') {
                updated.customBusinessUnit = '';
            }

            return updated;
        });
    };

    const handleManagerToggle = (value) => {
        setFormData((prev) => ({
            ...prev,
            isManager: value,
        }));
    };

    const handleBack = () => {
        navigate('/');
    };

    const validateForm = () => {
        if (!formData.firstName.trim()) return 'First Name is required';
        if (!formData.lastName.trim()) return 'Last Name is required';
        if (!formData.email.trim()) return 'Email is required';
        if (!finalTypeOfUserValue) return 'Type of User is required';
        if (!finalCompanyValue) return 'Company is required';
        if (!finalBusinessUnitValue) return 'Business Unit is required';
        if (!formData.country) return 'Country is required';
        if (!formData.managerName.trim()) return 'Manager Name is required';
        if (!formData.region) return 'Region is required';
        return null;
    };

    const buildCreateAccountPayload = () => ({
        email: formData.email.trim(),
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        region: formData.region,
        subregion: '',
        company: finalCompanyValue,
        typeOfUser: finalTypeOfUserValue,
        isManager: formData.isManager === 'Yes',
        canDemo: true,
        manager: formData.managerName.trim(),
        businessUnit: finalBusinessUnitValue,
        country: formData.country,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isCreateAccountLoading) return;

        const validationError = validateForm();

        if (validationError) {
            Swal.fire({
                title: 'Validation Error',
                text: validationError,
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }

        try {
            const data = await dispatch(createAccountAct(buildCreateAccountPayload()));

            Swal.fire({
                title: 'Request Submitted',
                text: data?.message || 'Your account request has been submitted successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
            }).then(() => {
                navigate('/');
            });
        } catch (error) {
            Swal.fire({
                title: 'Request Failed',
                text: error?.message || createAccountError || 'Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    return (
        <section className="acc-cre-page">
            <div className="acc-cre-wrapper">
                <div className="acc-cre-left">
                    <h1 className="acc-cre-title">Create Your Tech Central Account</h1>
                    <p className="acc-cre-subtitle">
                        Fill in your details to request access to Tech Central
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="acc-cre-section">
                            <div className="acc-cre-section-header">
                                <h3 className="acc-cre-section-title">Personal Information</h3>
                                <div className="acc-cre-section-line"></div>
                            </div>

                            <div className="acc-cre-grid-two">
                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="acc-cre-input"
                                    />
                                </div>

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="acc-cre-input"
                                    />
                                </div>
                            </div>

                            <div className="acc-cre-field acc-cre-field--after-grid">
                                <label className="acc-cre-label">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="acc-cre-input"
                                />
                            </div>
                        </div>

                        <div className="acc-cre-section">
                            <div className="acc-cre-section-header">
                                <h3 className="acc-cre-section-title">Organizational Information</h3>
                                <div className="acc-cre-section-line"></div>
                            </div>

                            <div className="acc-cre-grid-two">
                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Type of User</label>
                                    <select
                                        name="typeOfUser"
                                        value={formData.typeOfUser}
                                        onChange={handleChange}
                                        className="acc-cre-input acc-cre-select"
                                    >
                                        <option value="">{typeOfUserPlaceholder}</option>
                                        {accountTypeOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {showOtherTypeOfUserInput && (
                                    <div className="acc-cre-field acc-cre-field--dependent">
                                        <label className="acc-cre-label">Specify User Type</label>
                                        <input
                                            type="text"
                                            name="otherTypeOfUser"
                                            value={formData.otherTypeOfUser}
                                            onChange={handleChange}
                                            className="acc-cre-input"
                                            placeholder="Enter type of user"
                                        />
                                    </div>
                                )}

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Company</label>

                                    {showCompanyInput ? (
                                        <input
                                            type="text"
                                            name="company"
                                            value={formData.company}
                                            onChange={handleChange}
                                            className="acc-cre-input"
                                            placeholder="Enter company name"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={finalCompanyValue}
                                            className="acc-cre-input"
                                            disabled
                                            readOnly
                                        />
                                    )}
                                </div>

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Business Unit</label>
                                    <select
                                        name="businessUnit"
                                        value={formData.businessUnit}
                                        onChange={handleChange}
                                        className="acc-cre-input acc-cre-select"
                                    >
                                        <option value="DSOM">DSOM</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                {showBusinessUnitOtherInput && (
                                    <div className="acc-cre-field">
                                        <label className="acc-cre-label">Add Business Unit</label>
                                        <input
                                            type="text"
                                            name="customBusinessUnit"
                                            value={formData.customBusinessUnit}
                                            onChange={handleChange}
                                            className="acc-cre-input"
                                            placeholder="Enter business unit"
                                        />
                                    </div>
                                )}
                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Country</label>
                                    <select
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        className="acc-cre-input acc-cre-select"
                                    >
                                        <option value="">Select</option>
                                        {countryOptions.map((country) => (
                                            <option key={country} value={country}>
                                                {country}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Manager Name</label>
                                    <input
                                        type="text"
                                        name="managerName"
                                        value={formData.managerName}
                                        onChange={handleChange}
                                        className="acc-cre-input"
                                    />
                                </div>

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Region</label>
                                    <select
                                        name="region"
                                        value={formData.region}
                                        onChange={handleChange}
                                        className="acc-cre-input acc-cre-select"
                                    >
                                        <option value="">Select</option>
                                        {regionOptions.map((region) => (
                                            <option key={region} value={region}>
                                                {region}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="acc-cre-field">
                                    <label className="acc-cre-label">Are you a Manager?</label>
                                    <div className="acc-cre-toggle">
                                        <button
                                            type="button"
                                            className={`acc-cre-toggle-btn ${formData.isManager === 'Yes' ? 'acc-cre-toggle-btn-active' : ''
                                                }`}
                                            onClick={() => handleManagerToggle('Yes')}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            type="button"
                                            className={`acc-cre-toggle-btn ${formData.isManager === 'No' ? 'acc-cre-toggle-btn-active' : ''
                                                }`}
                                            onClick={() => handleManagerToggle('No')}
                                        >
                                            No
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p className="acc-cre-note">
                            By submitting this request, your details will be reviewed by an administrator
                        </p>

                        <div className="acc-cre-actions">
                            <button
                                type="button"
                                className="acc-cre-btn acc-cre-btn-secondary"
                                onClick={handleBack}
                            >
                                Back
                            </button>

                            <button
                                type="submit"
                                className="acc-cre-btn acc-cre-btn-primary"
                                disabled={isCreateAccountLoading}
                            >
                                {isCreateAccountLoading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="acc-cre-right">
                    <h2 className="acc-cre-right-title">How it Works</h2>

                    <div className="acc-cre-illustration">
                        <div className="acc-cre-illustration-card">
                            <FaClipboardCheck className="acc-cre-illustration-icon" />
                        </div>
                    </div>

                    <div className="acc-cre-steps">
                        <div className="acc-cre-step">Fill in your account details</div>
                        <div className="acc-cre-step">Your request will be reviewed by an administrator</div>
                        <div className="acc-cre-step">Once approved, you will receive your login credentials via email</div>
                        <div className="acc-cre-step">Only approved users will have access to Tech Central</div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AccountCreationForm;
